import { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { useStatsigClient } from '@statsig/react-bindings';

import { createQuestion, ERROR_SAVE_DRAFT, FOLDER_ID_CUSTOM } from '@api/instant_survey';
import { minQuestions } from '@api/surveys';

import { GATES } from '@constants';
import { ActionArea, Button, IconButton, OverlayTriggerTooltip, toast, Toolbar } from '@utilities';
import { ContractIcon, ExpandIcon, GPTOutlinedIcon, OverviewIcon } from '@utilities/icons';

import AddQuestionIconButton from './components/AddQuestionIconButton';
import ModalAIGeneratedQuestionnaire from './components/ModalAIGeneratedQuestionnaire';
import ModalInstantSurveyQuestion from './components/ModalInstantSurveyQuestion';
import ModalQuestionsLibrary from './components/ModalQuestionLibrary';
import ModalSurveyTemplate from './components/ModalSurveyTemplate';
import QuestionnaireList from './components/QuestionnaireList';
import SurveyToolbar from '../SurveyToolbar';

import { cleanupLogicAfterEdit, cleanupLogicAfterRemoval } from './utilities/helpers';
import { SurveyContext } from '../../utilities/context';

import styles from './_index.module.scss';

const Questionnaire = ({ onNext, onPrevious }) => {
  const {
    activeQuestions,
    currentQuestionCount,
    currentQuestionnairePrice,
    instantSurveyType,
    isResearchObjectivesTooLong,
    maxQuestions,
    saveInstantSurvey,
    setShowAIGenerationModal,
    setShowSurveyTemplateModal,
    setSurveyGoals,
    showAIGenerationModal,
    showSurveyTemplateModal,
    survey,
    updateSurvey,
  } = useContext(SurveyContext);

  const [generationError, setGenerationError] = useState(false);
  const [isDetailedQuestionView, setIsDetailedQuestionView] = useState(true);
  const [isDuplicatingQuestion, setIsDuplicatingQuestion] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showActions, setShowActions] = useState(true);
  const [showCustomQuestionModal, setShowCustomQuestionModal] = useState(false);
  const [showQuestionsLibraryModal, setShowQuestionsLibraryModal] = useState(false);

  const { permissions = {} } = useSelector((state) => state?.user?.data) || {};
  const { canUseAIGenerationQuestionnaire } = permissions;
  const { client } = useStatsigClient();
  const canSurveyTemplate = client.checkGate(GATES.SURVEY_TEMPLATES);

  const deletedQuestions = survey.questions.filter(({ _delete }) => _delete);
  const hasTurfQuestion = activeQuestions?.some(({ turf }) => Boolean(turf));
  const isTurfSurvey = instantSurveyType?.id === 'turf-v3-instant-survey-type';

  const firstErrorRef = useRef(null);

  useEffect(() => {
    setShowActions(previewQuestions.length === 0);
  }, [previewQuestions]);

  useEffect(() => {
    if (!survey?.questions?.length) return;

    const hasConceptImage =
      Array.isArray(survey?.groups) && survey.groups.some((group) => group?.conceptImage != null);

    if (!hasConceptImage && survey.questions.length > 0) {
      const hasQuestionsWithConceptImage = survey.questions.some(
        (question) => question.showConceptImage === true && !question._delete
      );

      if (hasQuestionsWithConceptImage) {
        const updatedQuestions = survey.questions.map((question) => {
          if (question._delete) return question;
          return {
            ...question,
            showConceptImage: false,
          };
        });

        updateSurvey({ questions: updatedQuestions });
      }
    }
  }, [survey?.groups, survey?.questions]);

  useEffect(() => {
    return () => {
      setSurveyGoals('');
    };
  }, [setSurveyGoals]);

  const createAIQuestion = async (question) => {
    const { aiGenerated, answers, text, type } = question;

    const controller = new AbortController();
    try {
      let data;
      data = await createQuestion({
        question: {
          aiGenerated,
          answers,
          folder: FOLDER_ID_CUSTOM,
          text,
          type,
        },
        signal: controller.signal,
      });
      return data;
    } catch (error) {
      if (!controller.signal.aborted) {
        toast('Error creating question', { status: 'error' });
      }
    }
  };

  const handleAcceptPreview = async () => {
    const existingQuestions = survey.questions.map((question) => ({ ...question, _delete: true }));
    const createdQuestions = await Promise.all(
      previewQuestions.map((question) => createAIQuestion(question))
    );
    const updatedQuestions = [...createdQuestions, ...existingQuestions];
    updateSurvey({
      questionLogic: {},
      questions: updatedQuestions,
      surveyTemplateId: null,
    });
    setGenerationError(false);
    setPreviewQuestions([]);
  };

  const handleAddAIQuestions = (questions) => {
    setPreviewQuestions(questions);
  };

  const handleCancelPreview = () => {
    setPreviewQuestions([]);
    setSurveyGoals('');
  };

  const handleAddLibraryQuestions = (questions) => {
    updateSurvey({
      questions: [
        ...activeQuestions,
        ...questions,
        ...deletedQuestions.filter(
          // extra .filter checks for questions being added that were previously removed
          ({ id: existingQuestionId }) =>
            !questions.some(
              ({ id: selectedQuestionId }) => existingQuestionId === selectedQuestionId
            )
        ),
      ],
    });
    setGenerationError(false);
    handleQuestionLibraryModalClose();
  };

  const handleAddSurveyTemplate = (template) => {
    const existingQuestions = survey.questions
      // only mark questions for deletion that are not in the template (delete overrides the add)
      .filter(({ id }) => !template.questions.some((question) => question.id === id))
      .map((question) => ({ ...question, _delete: true }));
    const updatedQuestions = [...template.questions, ...existingQuestions];
    updateSurvey({
      questionLogic: {},
      questions: updatedQuestions,
      surveyTemplateId: template.id,
    });
    setPreviewQuestions([]);
    setGenerationError(false);
    handleQuestionSurveyTemplateClose();
  };

  const handleQuestionCreate = (question) => {
    updateSurvey({ questions: [...activeQuestions, question, ...deletedQuestions] });
    setGenerationError(false);
    handleQuestionModalClose();
  };

  const handleQuestionDuplicate = (question) => {
    setIsDuplicatingQuestion(true);
    setSelectedQuestion(question);
    setShowCustomQuestionModal(true);
  };

  const handleQuestionEdit = (question) => {
    setSelectedQuestion(question);
    setShowCustomQuestionModal(true);
  };

  const handleQuestionLibraryModalClose = () => {
    setShowQuestionsLibraryModal(false);
    setSelectedQuestion(null);
  };

  const handleQuestionSurveyTemplateClose = () => {
    setShowSurveyTemplateModal(false);
  };

  const handleQuestionModalClose = () => {
    setShowCustomQuestionModal(false);

    setIsDuplicatingQuestion(false);
    setSelectedQuestion(null);
  };

  const handleQuestionRemove = (question) => {
    const updatedQuestions = [...survey.questions];
    const questionIndex = updatedQuestions.findIndex(({ id }) => id === question.id);

    const [removedQuestion] = updatedQuestions.splice(questionIndex, 1);
    removedQuestion._delete = true;
    updateSurvey({
      questionLogic: cleanupLogicAfterRemoval({
        questionId: question.id,
        questionLogic: survey.questionLogic,
        questions: updatedQuestions,
      }),
      questions: [...updatedQuestions, removedQuestion],
      // INSRV-2109: remove surveyTemplateId if all questions are removed from the questionnaire
      surveyTemplateId:
        updatedQuestions.filter(({ _delete }) => !_delete).length > 0
          ? survey.surveyTemplateId
          : null,
    });
  };

  const handleQuestionUpdate = (question) => {
    const questionIndex = survey.questions.findIndex(({ id }) => id === selectedQuestion?.id);
    const updatedQuestions = [...survey.questions];

    const [removedQuestion] = updatedQuestions.splice(questionIndex, 1, question);
    if (removedQuestion.id !== question.id) {
      // a recommended question became a custom question, remove the original question
      removedQuestion._delete = true;
      updatedQuestions.push(removedQuestion);
    }

    if (questionIndex >= 0) {
      updateSurvey({
        questionLogic: cleanupLogicAfterEdit({
          originalQuestionId: selectedQuestion.id,
          question,
          questionLogic: survey.questionLogic,
          questions: updatedQuestions,
        }),
        questions: [...updatedQuestions],
      });
    }
    handleQuestionModalClose();
  };

  const onContinue = async () => {
    if (isTurfSurvey && !hasTurfQuestion) {
      toast('Please add at least one turf question before continuing.', { status: 'error' });
      return;
    }
    const isLogicValid = validateQuestionLogic();
    if (!isLogicValid) {
      if (firstErrorRef.current) {
        firstErrorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
      return;
    }

    setIsSaving(true);
    try {
      await saveInstantSurvey();
    } catch (error) {
      toast(ERROR_SAVE_DRAFT, { status: 'error' });
      setIsSaving(false);
      return;
    }

    onNext();
  };

  const validateQuestionLogic = () => {
    const modifiedQuestionLogic = { ...survey.questionLogic };
    let isValid = true;
    for (const questionId in modifiedQuestionLogic) {
      const question = modifiedQuestionLogic[questionId];

      if (question.skip) {
        for (const row of question.skip.logic) {
          if (row.hasOwnProperty('url_variable')) {
            const quotaGroupIds = survey.groups.map(({ id }) => id);
            if (!row.values || row.values.length === 0) {
              row.hasAnswerError = true;
              isValid = false;
            } else {
              let quotaGroupsValid = true;
              for (const groupID of row.values) {
                if (!quotaGroupIds.includes(Number(groupID))) {
                  quotaGroupsValid = false;
                }
              }
              if (!quotaGroupsValid) {
                row.hasAnswerError = true;
                isValid = false;
              } else {
                row.hasAnswerError = false;
              }
            }
          } else if (row.hasOwnProperty('comparison_value')) {
            if (!row.question?.id) {
              row.hasQuestionError = true;
              row.isFirstQuestionError = isValid;
              isValid = false;
            } else {
              row.hasQuestionError = false;
            }
            if (!row.comparison_value) {
              row.hasAnswerError = true;
              row.isFirstAnswerError = isValid;
              isValid = false;
            } else {
              row.hasAnswerError = false;
            }
          } else {
            if (!row.question?.id) {
              row.hasQuestionError = true;
              row.isFirstQuestionError = isValid;
              isValid = false;
            } else {
              row.hasQuestionError = false;
            }
            if (row.answers.length === 0) {
              row.hasAnswerError = true;
              row.isFirstAnswerError = isValid;
              isValid = false;
            } else {
              row.hasAnswerError = false;
            }
          }
        }
      }

      if (question.disqualify) {
        if (question.disqualify.answers.length === 0) {
          question.disqualify.isFirstAnswerError = isValid;
          question.disqualify.hasAnswerError = true;
          isValid = false;
        } else {
          question.disqualify.hasAnswerError = false;
        }
      }
    }

    updateSurvey({ questionLogic: modifiedQuestionLogic });
    return isValid;
  };

  const isMinimumQuestionsThresholdUnmet = currentQuestionCount < minQuestions;
  const isContinueDisabled = isMinimumQuestionsThresholdUnmet || isResearchObjectivesTooLong;

  return (
    <>
      <ActionArea
        buttons={[
          previewQuestions.length > 0
            ? [
                <Button
                  data-testid="cancel-button"
                  key="cancel-button"
                  onClick={handleCancelPreview}
                  text="Cancel"
                  variant="secondary"
                />,
                <Button
                  data-testid="insert-button"
                  key="insert-button"
                  onClick={handleAcceptPreview}
                  text="Insert Questions"
                  variant="ai"
                />,
              ]
            : [
                <Button
                  data-testid="previous-button"
                  key="previous-button"
                  onClick={onPrevious}
                  text="Previous"
                  variant="secondary"
                />,
                <Button
                  data-testid="review-button"
                  isDisabled={isContinueDisabled}
                  isLoading={isSaving}
                  key="review-button"
                  onClick={onContinue}
                  text="Go To Review"
                />,
              ],
        ]}
        className={styles['questionnaire']}
      >
        <SurveyToolbar isSaveAsDraftDisabled={isResearchObjectivesTooLong} />
        <div className={styles['questionnaire-pricing']}>
          <Toolbar
            tools={[
              ...(showActions
                ? [
                    <AddQuestionIconButton
                      onAddQuestion={() => setShowQuestionsLibraryModal(true)}
                      onCreateQuestion={() => setShowCustomQuestionModal(true)}
                    />,
                    ...(canUseAIGenerationQuestionnaire
                      ? [
                          <div>
                            <IconButton
                              onClick={() => {
                                setShowAIGenerationModal(true);
                                setSurveyGoals('');
                              }}
                              tooltip="AI Generated Questionnaire"
                            >
                              <GPTOutlinedIcon />
                            </IconButton>
                          </div>,
                        ]
                      : []),
                    ...(canSurveyTemplate
                      ? [
                          <IconButton
                            data-log="SurveyTemplates_Open"
                            data-log-properties={JSON.stringify({ type: instantSurveyType.id })}
                            onClick={() => setShowSurveyTemplateModal(true)}
                            tooltip="Survey Templates"
                          >
                            <OverviewIcon />
                          </IconButton>,
                        ]
                      : []),
                    <OverlayTriggerTooltip
                      content={'Change to ' + (isDetailedQuestionView ? 'Overview' : 'Detailed')}
                    >
                      <button
                        data-testid="change-view-button"
                        onClick={() => setIsDetailedQuestionView(!isDetailedQuestionView)}
                      >
                        {isDetailedQuestionView ? <ContractIcon /> : <ExpandIcon />}
                        {!canSurveyTemplate && <span>View</span>}
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
            ]}
          >
            <p>{currentQuestionnairePrice?.description}</p>
          </Toolbar>
        </div>
        <QuestionnaireList
          firstErrorRef={firstErrorRef}
          generationError={generationError}
          isDetailedQuestionView={isDetailedQuestionView}
          onAcceptPreview={handleAcceptPreview}
          onCreateQuestion={() => setShowCustomQuestionModal(true)}
          onDuplicateQuestion={handleQuestionDuplicate}
          onEditQuestion={handleQuestionEdit}
          onRemoveQuestion={handleQuestionRemove}
          onReprompt={() => {
            setGenerationError(false);
            setShowAIGenerationModal(true);
          }}
          questions={previewQuestions.length > 0 ? previewQuestions : activeQuestions}
          showActions={showActions}
          showPreviewBanner={previewQuestions.length > 0}
        />

        <div className={styles['questionnaire-list-actions']}>
          {!generationError && showActions && (
            <AddQuestionIconButton
              onAddQuestion={() => setShowQuestionsLibraryModal(true)}
              onCreateQuestion={() => setShowCustomQuestionModal(true)}
            />
          )}
        </div>
      </ActionArea>

      {showAIGenerationModal && (
        <ModalAIGeneratedQuestionnaire
          initialSurveyLength={previewQuestions.length}
          onAddAIQuestions={handleAddAIQuestions}
          onClose={() => setShowAIGenerationModal(false)}
          onGenerationError={(val) => {
            setGenerationError(val);
            if (val) setPreviewQuestions([]);
          }}
        />
      )}
      {showCustomQuestionModal && (
        <ModalInstantSurveyQuestion
          currentQuestionCount={currentQuestionCount}
          id={selectedQuestion?.id}
          isDuplicate={isDuplicatingQuestion}
          isReadOnly={
            !isDuplicatingQuestion &&
            selectedQuestion?.folder &&
            selectedQuestion?.folder !== FOLDER_ID_CUSTOM
          }
          maxQuestions={maxQuestions}
          onClose={handleQuestionModalClose}
          onCreate={handleQuestionCreate}
          onUpdate={handleQuestionUpdate}
          originalQuestion={selectedQuestion}
          survey={survey}
          surveyType={instantSurveyType}
        />
      )}
      {showQuestionsLibraryModal && (
        <ModalQuestionsLibrary
          maxQuestions={maxQuestions}
          onClose={handleQuestionLibraryModalClose}
          onSelectQuestions={handleAddLibraryQuestions}
          selectedQuestions={activeQuestions}
          surveyType={instantSurveyType}
        />
      )}
      {showSurveyTemplateModal && (
        <ModalSurveyTemplate
          hasWarning={activeQuestions.length > 0}
          onClose={handleQuestionSurveyTemplateClose}
          onSelectSurveyTemplate={handleAddSurveyTemplate}
          surveyType={instantSurveyType}
        />
      )}
    </>
  );
};

Questionnaire.propTypes = {
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
};

export default Questionnaire;
